import { UsersTableProps } from './users-table.types'
import styles from './users-table.module.scss'
import cl from 'classnames'
import { observer } from 'mobx-react-lite'
import {
  TableContainer,
  TableHeader,
  TableBodyContainer,
  TableBodyLine,
  PaginationButtons,
  Text1,
  Button,
  NoneLabel,
  PlusNumber,
} from '@touche-lts/panel-ui/dist'
import { usersTableWidths } from './users-table.utils'
import { useRootStore } from 'mobx-stores/store-provider'
import { useEffect, useState } from 'react'
import { UsersTableSortProps } from 'mobx-stores'
import moment from 'moment'
import { PictureNameLine } from 'components'
import { IconMinusCircle, IconPlusCircle } from 'assets/icons'
import { AssignedStoresTable } from 'pages/user-management/user-profile-page/stores-section/assigned-stores-table'
import { useGetScrollWidth } from 'hooks/custom-hooks'

const UsersTableComponent = (props: UsersTableProps) => {
  const { className, style } = props

  const {
    usersTableStore: {
      onTableMount,
      searchProps,
      onSearchPropsChange,
      userRolesAsSelectOptions,
      isColumnMarked,
      getSortDirection,
      handleSortDirectionChange,
      paginationInfo,
      searchResults,
      onPageSelect
    },
    userProfilePageStore: {
      hasAssignedStores,
      loadUser,
      clearStore
    },
    routingStore: { push }
  } = useRootStore()

  const [expandedUserId, setExpandedUserId] = useState<number>(null)
  useEffect(() => {
    onTableMount()
    if (expandedUserId) {
      loadUser(expandedUserId)
    }

    return () => {
      clearStore()
    }

  }, [clearStore, expandedUserId, loadUser, onTableMount])

  const {scrollX,handleScroll} = useGetScrollWidth()

  return (
    <TableContainer className={cl(styles.container, className, styles.thinScroll)} style={{...style, overflowX: 'auto'}} onScroll={handleScroll}>

      <TableHeader
        tableColumns={[
          {
            label: 'users',
            widthPx: usersTableWidths.users,
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.name,
              onChange: name => onSearchPropsChange({ name })
            },
            isColumnMarked: isColumnMarked(UsersTableSortProps.NAME),
            sortDirection: getSortDirection(UsersTableSortProps.NAME),
            setSortDirection: handleSortDirectionChange(
              UsersTableSortProps.NAME
            )
          },
          {
            label: 'user role',
            widthPx: usersTableWidths.role,
            selectProps: {
              noneLabel: 'All',
              options: userRolesAsSelectOptions,
              value: searchProps.roleId,
              onChange: roleId => onSearchPropsChange({ roleId })
            },
            isColumnMarked: isColumnMarked(UsersTableSortProps.ROLE),
            sortDirection: getSortDirection(UsersTableSortProps.ROLE),
            setSortDirection: handleSortDirectionChange(
              UsersTableSortProps.ROLE
            )
          },
          {
            label: 'email',
            widthPx: usersTableWidths.email,
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.email,
              onChange: email => onSearchPropsChange({ email })
            },
            isColumnMarked: isColumnMarked(UsersTableSortProps.EMAIL),
            sortDirection: getSortDirection(UsersTableSortProps.EMAIL),
            setSortDirection: handleSortDirectionChange(
              UsersTableSortProps.EMAIL
            )
          },
          {
            label: 'store',
            widthPx: usersTableWidths.store,
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.storeName,
              onChange: storeName => onSearchPropsChange({ storeName })
            },
            isColumnMarked: isColumnMarked(UsersTableSortProps.STORE),
            sortDirection: getSortDirection(UsersTableSortProps.STORE),
            setSortDirection: handleSortDirectionChange(
              UsersTableSortProps.STORE
            )
          },
          {
            label: 'added on',
            widthPx: usersTableWidths.addedOn,
            periodFilterProps: {
              period: searchProps.addedOnPeriod,
              setPeriod: addedOnPeriod => onSearchPropsChange({ addedOnPeriod })
            },
            isColumnMarked: isColumnMarked(UsersTableSortProps.ADDED_ON),
            sortDirection: getSortDirection(UsersTableSortProps.ADDED_ON),
            setSortDirection: handleSortDirectionChange(
              UsersTableSortProps.ADDED_ON
            )
          }
        ]}
      />
      <TableBodyContainer className={styles.tableBodyContainer}>
        {searchResults.map(tableEntry => {
          const isExpanded = expandedUserId === tableEntry.id
          const hasAssignedStore = Boolean(tableEntry.stores?.length)
          return (
            <TableBodyLine
              key={tableEntry.id}
              cells={[
                {
                  widthPx: usersTableWidths.users,
                  children: (
                    <PictureNameLine
                      imageSrc={tableEntry.imageUrl}
                      name={tableEntry.fullName}
                    />
                  ),
                  onClick: () => push(`/user-management/${tableEntry.id}`)
                },
                {
                  widthPx: usersTableWidths.role,
                  children: (
                    <Text1 overflowEllipsis>{tableEntry.role?.label}</Text1>
                  )
                },
                {
                  widthPx: usersTableWidths.email,
                  children: (
                    <Text1 overflowEllipsis>{tableEntry.emailAddress}</Text1>
                  )
                },
                {
                  widthPx: usersTableWidths.store,
                  children: (
                    <>
                      <Button
                        preset='icon'
                        onClick={() => setExpandedUserId(isExpanded ? null : tableEntry.id)}
                      >
                        {isExpanded
                          ?
                          <IconMinusCircle />
                          : tableEntry.stores.length > 0 ?
                            <PlusNumber num={tableEntry.stores?.length} /> : <></>

                        }
                      </Button>
                      <Button
                        preset='icon'
                        onClick={() => push(
                          hasAssignedStore ?
                            '/user-management'
                            : `/user-management/${tableEntry.id}`
                        )}
                      >
                        <></>
                      </Button>
                    </>
                  )
                },
                {
                  widthPx: usersTableWidths.addedOn,
                  children: (
                    <Text1 overflowEllipsis>
                      {moment(tableEntry.addedOn).format('DD.MM.YY')}
                    </Text1>
                  )
                }
              ]}
              isExpanded={isExpanded}
              expandComponent={hasAssignedStores && (
                <AssignedStoresTable style={{ padding: 40 }} />
              )}
            />
          )
        })}
      </TableBodyContainer>
      <PaginationButtons alwaysShow {...paginationInfo} onPageSelect={onPageSelect} style={{width: scrollX}}/>
    </TableContainer>
  )
}

const UsersTable = observer(UsersTableComponent)
export { UsersTable }
